import { useEffect, useState } from "react";
import { api, formatErr } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { handleSimpleFormNav } from "@/lib/keyboardNav";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Pencil, Trash2, MapPin } from "lucide-react";
import { toast } from "sonner";
import ImportExportBar from "@/components/ImportExportBar";
import { PaginationBar, usePagination } from "@/components/Pagination";

const empty = { sector_code: "", name: "", description: "", status: "Active" };

export default function SectorMaster() {
  const [list, setList] = useState([]);
  const pg = usePagination(list);
  const [open, setOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  const [f, setF] = useState(empty);

  const load = () => api.get("/sectors").then(r => setList(r.data));
  useEffect(() => { load(); }, []);

  const save = async () => {
    try {
      const payload = { sector_code: f.sector_code || null, name: f.name, description: f.description || null, status: f.status };
      if (editId) await api.patch(`/sectors/${editId}`, payload);
      else await api.post("/sectors", payload);
      toast.success(editId ? "Sector updated" : "Sector added");
      setF(empty); setEditId(null); setOpen(false); load();
    } catch (e) { toast.error(formatErr(e)); }
  };

  const edit = (s) => {
    setF({ sector_code: s.sector_code || "", name: s.name, description: s.description || "", status: s.status || "Active" });
    setEditId(s.id); setOpen(true);
  };

  const del = async (s) => {
    if (!confirm(`Delete sector ${s.sector_code} — ${s.name}? This cannot be undone.`)) return;
    try { await api.delete(`/sectors/${s.id}`); toast.success("Sector deleted"); load(); }
    catch (e) { toast.error(formatErr(e)); }
  };

  return (
    <div className="p-8">
      <div className="flex items-end justify-between mb-6">
        <div>
          <div className="text-primary text-xs uppercase tracking-[0.3em] mb-2 flex items-center gap-2"><MapPin className="w-3 h-3"/> Inventory · Masters</div>
          <h1 className="font-display font-black text-4xl uppercase tracking-tighter">Sector Master</h1>
        </div>
        <div className="flex items-center gap-2">
          <ImportExportBar masterKey="sectors" filters={{}} onImported={load} />
          <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) { setF(empty); setEditId(null); } }}>
            <DialogTrigger asChild>
              <Button data-testid="add-sector-btn" className="rounded-sm bg-primary text-primary-foreground uppercase tracking-[0.15em] font-bold text-xs">
                <Plus className="w-4 h-4 mr-1"/> New Sector
              </Button>
            </DialogTrigger>
            <DialogContent className="rounded-sm bg-card border-border max-w-lg">
              <DialogHeader><DialogTitle className="font-display uppercase">{editId ? "Edit" : "New"} Sector</DialogTitle></DialogHeader>
              <div className="space-y-3" onKeyDownCapture={handleSimpleFormNav}>
                <div>
                  <Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Sector Code (blank = auto-generated)</Label>
                  <Input data-testid="sector-code" value={f.sector_code} onChange={(e) => setF(x => ({ ...x, sector_code: e.target.value.toUpperCase() }))} className="mt-1 rounded-sm bg-secondary border-border font-mono uppercase" placeholder="e.g. SEC-ENGINE" />
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Sector Name *</Label>
                  <Input data-testid="sector-name" value={f.name} onChange={(e) => setF(x => ({ ...x, name: e.target.value }))} className="mt-1 rounded-sm bg-secondary border-border" placeholder="e.g. Engine" />
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Description</Label>
                  <Input data-testid="sector-desc" value={f.description} onChange={(e) => setF(x => ({ ...x, description: e.target.value }))} className="mt-1 rounded-sm bg-secondary border-border" />
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Status</Label>
                  <Select value={f.status} onValueChange={(v) => setF({ ...f, status: v })}>
                    <SelectTrigger data-testid="sector-status" className="mt-1 rounded-sm bg-secondary border-border"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Active">Active</SelectItem>
                      <SelectItem value="Inactive">Inactive</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <Button data-testid="save-sector-btn" onClick={save} disabled={!f.name} className="w-full rounded-sm bg-primary text-primary-foreground uppercase tracking-[0.15em]">
                  {editId ? "Update" : "Save"}
                </Button>
              </div>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      <div className="border border-border bg-card rounded-sm overflow-hidden overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-secondary text-[10px] uppercase tracking-[0.2em] text-muted-foreground">
            <tr>
              <th className="text-left p-3">Sector Code</th>
              <th className="text-left p-3">Sector Name</th>
              <th className="text-left p-3">Description</th>
              <th className="text-left p-3">Status</th>
              <th className="text-right p-3">Actions</th>
            </tr>
          </thead>
          <tbody>
            {list.length === 0 && <tr><td colSpan={5} className="p-6 text-center text-muted-foreground">No sectors yet</td></tr>}
            {pg.pageItems.map(s => (
              <tr key={s.id} data-testid={`sector-row-${s.id}`} className="border-t border-border hover:bg-secondary/50">
                <td className="p-3 font-mono text-xs text-primary">{s.sector_code}</td>
                <td className="p-3">{s.name}</td>
                <td className="p-3 text-xs text-muted-foreground">{s.description || "-"}</td>
                <td className="p-3">
                  <span className={`px-2 py-1 text-[10px] uppercase tracking-wider rounded-sm border ${(s.status || "Active") === "Active" ? "text-green-500 bg-green-500/10 border-green-500/40" : "text-muted-foreground bg-secondary border-border"}`}>{s.status || "Active"}</span>
                </td>
                <td className="p-3 text-right space-x-1 whitespace-nowrap">
                  <Button data-testid={`edit-sector-${s.id}`} size="sm" variant="ghost" onClick={() => edit(s)} className="h-7 w-7 p-0"><Pencil className="w-3 h-3"/></Button>
                  <Button data-testid={`del-sector-${s.id}`} size="sm" variant="ghost" onClick={() => del(s)} className="h-7 w-7 p-0 text-destructive"><Trash2 className="w-3 h-3"/></Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        <PaginationBar pg={pg}/>
      </div>
    </div>
  );
}
